import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CreditCard } from 'lucide-react';

interface TransactionHistoryTableProps {
  organizationId?: string;
  clubId?: string;
  userId?: string;
}

export const TransactionHistoryTable: React.FC<TransactionHistoryTableProps> = ({
  organizationId,
  clubId,
  userId
}) => {
  const { data: transactions, isLoading } = useQuery({
    queryKey: ['transaction-history', organizationId, clubId, userId],
    queryFn: async () => {
      let query = supabase
        .from('payment_transactions')
        .select('*')
        .order('created_at', { ascending: false });

      if (userId) {
        query = query.or(`payer_id.eq.${userId},recipient_id.eq.${userId}`);
      } else if (clubId) {
        query = query.eq('metadata->>club_id', clubId);
      } else if (organizationId) {
        query = query.eq('metadata->>organization_id', organizationId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return <div>Loading transaction history...</div>;
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'succeeded': return 'default';
      case 'pending': return 'secondary';
      case 'failed': return 'destructive';
      case 'refunded': return 'outline';
      default: return 'outline';
    }
  };

  const getPaymentTypeLabel = (type: string) => {
    switch (type) {
      case 'club_fee': return 'Club Fee';
      case 'coach_salary': return 'Coach Salary';
      case 'coach_clinic': return 'Coach Clinic';
      default: return type;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Transaction History
        </CardTitle>
        <CardDescription>
          Complete history of payment transactions
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {transactions && transactions.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Net Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>
                    {new Date(transaction.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {getPaymentTypeLabel(transaction.payment_type)}
                  </TableCell>
                  <TableCell>
                    ${(transaction.gross_amount / 100).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    ${((transaction.platform_commission + (transaction.club_commission || 0)) / 100).toFixed(2)}
                  </TableCell>
                  <TableCell className="font-medium">
                    ${(transaction.net_amount / 100).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(transaction.status)}>
                      {transaction.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No transactions found.
          </div>
        )}
      </CardContent>
    </Card>
  );
};